import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from './FormField';
import { PlusIcon, TrashIcon, InfoIcon } from 'lucide-react';
export interface MetadataItem {
  key: string;
  value: string;
  isPreset?: boolean;
  required?: boolean;
  type?: string;
  description?: string;
}
interface MetadataEditorProps {
  metadata: MetadataItem[];
  onChange: (metadata: MetadataItem[]) => void;
}
export function MetadataEditor({
  metadata,
  onChange
}: MetadataEditorProps) {
  const {
    t
  } = useTranslation('common');
  const [showPresetInfo, setShowPresetInfo] = useState(false);
  // Get preset fields from "settings" - in a real app this would come from localStorage or API
  // For this demo, we'll hardcode some preset fields
  const [presetFields] = useState([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts and communication"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number for this payment'
  }]);
  // Add preset fields if they're not already in the metadata
  useEffect(() => {
    const existingKeys = metadata.map(item => item.key);
    const missingPresets = presetFields.filter(preset => !existingKeys.includes(preset.key));
    if (missingPresets.length > 0) {
      onChange([...metadata, ...missingPresets]);
    }
  }, []);
  const getFieldType = (type: string | undefined) => {
    switch (type) {
      case 'email':
        return 'email';
      case 'number':
        return 'number';
      case 'phone':
        return 'tel';
      case 'amount':
        return 'number';
      case 'date':
        return 'date';
      case 'url':
        return 'url';
      default:
        return 'text';
    }
  };
  return <FormField label={t('ui.MetadataEditor.attrs.custom_metadata')} tooltip={t('ui.MetadataEditor.attrs.add_additional_data_to_track_with')} data-id="element-14778">
      {/* Preset fields info */}
      {metadata.some(item => item.isPreset) && <div className="mb-3" data-id="element-14779">
          <button type="button" onClick={() => setShowPresetInfo(!showPresetInfo)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-14780">
            <InfoIcon size={14} className="mr-1" data-id="element-14781" />
            {showPresetInfo ? 'Hide preset fields info' : 'About preset fields'}
          </button>
          {showPresetInfo && <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-md text-sm text-blue-800" data-id="element-14782">
              <p data-id="element-14783">
                {t('ui.MetadataEditor.preset_fields_are_configured_in_your')}
              </p>
              <p className="mt-1" data-id="element-14784">
                {t('ui.MetadataEditor.required_preset_fields_cannot_be_removed')}
              </p>
            </div>}
        </div>}
      <div className="mt-2" data-id="element-14785">
        <button type="button" onClick={() => onChange([...metadata, {
        key: '',
        value: ''
      }])} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-14786">
          <PlusIcon size={16} className="mr-1" data-id="element-14787" />
          {t('ui.MetadataEditor.add_custom_field')}
        </button>
      </div>
      {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-14788">
          {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-14789">
              <div className="w-1/2" data-id="element-14790">
                <input type="text" aria-label={t('ui.MetadataEditor.attrs.key')} className={`block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 ${item.isPreset ? 'bg-gray-50' : ''}`} placeholder={t('ui.MetadataEditor.attrs.key')} value={item.key} onChange={e => {
            if (item.isPreset) return; // Prevent editing preset keys
            const updatedMetadata = [...metadata];
            updatedMetadata[index].key = e.target.value;
            onChange(updatedMetadata);
          }} readOnly={item.isPreset} data-id="element-14791" />

                {item.description && <div className="mt-1 text-xs text-gray-500" data-id="element-14792">
                    {item.description}
                  </div>}
              </div>
              <div className="w-1/2" data-id="element-14793">
                <input type={getFieldType(item.type)} aria-label={item.isPreset ? `Value for ${item.key}` : 'Metadata value'} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={item.isPreset ? `Enter ${item.key}` : 'Value'} value={item.value} onChange={e => {
            const updatedMetadata = [...metadata];
            updatedMetadata[index].value = e.target.value;
            onChange(updatedMetadata);
          }} data-id="element-14794" />

                {item.required && <div className="mt-1 text-xs text-red-500" data-id="element-14795">
                    {t('ui.MetadataEditor.required')}
                  </div>}
              </div>
              <button type="button" onClick={() => {
          // Only allow removing non-preset fields or optional preset fields
          if (item.isPreset && item.required) return;
          const updatedMetadata = [...metadata];
          updatedMetadata.splice(index, 1);
          onChange(updatedMetadata);
        }} className={`p-2 ${item.isPreset && item.required ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-500'}`} disabled={item.isPreset && item.required} data-id="element-14796">
                <TrashIcon size={18} data-id="element-14797" />
              </button>
            </div>)}
        </div>}
    </FormField>;
}